import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Italic, Eyebrow } from "./_primitives";
import { FOUNDER_PROBLEMS, INVESTOR_PROBLEMS } from "../../data/sparkseed";
import { Lightbulb, Users2, BookOpen, EyeOff, Clock, Target, Microscope, Wrench } from "lucide-react";

const founderIcons = [Lightbulb, Users2, BookOpen, EyeOff, Clock];
const investorIcons = [Target, Microscope, Wrench];

export default function ProblemTabs() {
  const [tab, setTab] = useState("founder");

  return (
    <section
      data-testid="problem-section"
      className="bg-[#F7F8EF] relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <Eyebrow>The Disconnect</Eyebrow>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-bold text-[#0F2818]">
            The Problem We're <Italic>Solving</Italic>.
          </h2>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex p-1.5 bg-[#DCE8C9] rounded-full border border-[#14401F]/10">
            <button
              data-testid="problem-tab-founder"
              onClick={() => setTab("founder")}
              className={`px-5 sm:px-7 py-3 rounded-full text-sm font-semibold transition-all ${
                tab === "founder"
                  ? "bg-[#14401F] text-white shadow-md"
                  : "text-[#0F2818]/70 hover:text-[#0F2818]"
              }`}
            >
              The Founder's Problem
            </button>
            <button
              data-testid="problem-tab-investor"
              onClick={() => setTab("investor")}
              className={`px-5 sm:px-7 py-3 rounded-full text-sm font-semibold transition-all ${
                tab === "investor"
                  ? "bg-[#14401F] text-white shadow-md"
                  : "text-[#0F2818]/70 hover:text-[#0F2818]"
              }`}
            >
              The Investor's Problem
            </button>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {tab === "founder" ? (
            <motion.div
              key="founder"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35 }}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
                {FOUNDER_PROBLEMS.map((p, i) => {
                  const Icon = founderIcons[i];
                  return (
                    <div
                      key={i}
                      data-testid={`founder-problem-${i}`}
                      className="bg-white rounded-2xl p-6 border border-[#14401F]/10 hover:-translate-y-1 hover:shadow-lg transition-all"
                    >
                      <div className="h-11 w-11 rounded-xl bg-[#DCE8C9] text-[#14401F] flex items-center justify-center mb-5">
                        <Icon className="h-5 w-5" />
                      </div>
                      <h3 className="font-serif-display text-lg font-bold text-[#0F2818] mb-2 leading-snug">
                        {p.title}
                      </h3>
                      <p className="text-sm text-[#4A5A4F] leading-relaxed">{p.desc}</p>
                    </div>
                  );
                })}
              </div>

              <div className="mt-10 bg-[#14401F] rounded-3xl p-8 lg:p-10 text-center">
                <p className="font-serif-display text-2xl sm:text-3xl text-white leading-snug">
                  <span className="text-[#B8D958] font-bold">90%</span> of Indian
                  startups fail within 5 years — not from lack of talent, but
                  lack of infrastructure.{" "}
                  <span className="text-[#B8D958] font-bold">Only 1 in 12</span>{" "}
                  early-stage startups get institutional backing before revenue.
                </p>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="investor"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35 }}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {INVESTOR_PROBLEMS.map((p, i) => {
                  const Icon = investorIcons[i];
                  return (
                    <div
                      key={i}
                      data-testid={`investor-problem-${i}`}
                      className="bg-white rounded-2xl p-7 border border-[#14401F]/10 hover:-translate-y-1 hover:shadow-lg transition-all"
                    >
                      <div className="h-12 w-12 rounded-xl bg-[#DCE8C9] text-[#14401F] flex items-center justify-center mb-5">
                        <Icon className="h-5 w-5" />
                      </div>
                      <span className="text-xs uppercase tracking-widest font-semibold text-[#14401F]">
                        {p.title}
                      </span>
                      <p className="mt-3 text-[#0F2818] leading-relaxed">{p.desc}</p>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-14 max-w-3xl mx-auto text-center">
          <p className="font-serif-display text-xl sm:text-2xl text-[#0F2818] italic leading-snug">
            "The market needs a fund that combines the discipline of a VC with
            the hands-on intensity of a studio.{" "}
            <span className="not-italic font-semibold text-[#14401F]">
              That's SparkSeed.
            </span>
            "
          </p>
        </div>
      </div>
    </section>
  );
}
